import { useState } from 'react';
import { Button } from '../components/button/Button';
import { Card } from '../components/card/Card';
import { PageShell } from '../components/PageShell';
import {
  adminFetchTemplates,
  adminCreateTemplate,
  adminUpdateTemplate,
  adminDeleteTemplate,
  adminFetchPlayedWeapons,
  adminPromoteWeapon,
  type PlayedWeapon,
} from '../api/admin';
import type { WeaponTemplateResponse } from '../api/games';
import styles from './AdminPage.module.css';

const DIFFICULTIES = ['easy', 'moderate', 'hard'] as const;
const LOCATIONS = ['anywhere', 'cabin', 'university', 'travel'] as const;
const LANGUAGES = ['en', 'no'] as const;

type EditState = {
  name: string;
  description: string;
  difficulty: string;
  location: string;
  language: string;
};

const blankEdit = (): EditState => ({
  name: '',
  description: '',
  difficulty: 'easy',
  location: 'anywhere',
  language: 'en',
});

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');

  const [templates, setTemplates] = useState<WeaponTemplateResponse[]>([]);
  const [played, setPlayed] = useState<PlayedWeapon[]>([]);
  const [tab, setTab] = useState<'templates' | 'played'>('templates');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>(blankEdit());
  const [newTemplate, setNewTemplate] = useState<EditState>(blankEdit());
  const [showNew, setShowNew] = useState(false);

  const [promoteMeta, setPromoteMeta] = useState<Record<number, EditState>>({});

  const load = async (pw: string) => {
    const [tmpl, play] = await Promise.all([adminFetchTemplates(pw), adminFetchPlayedWeapons(pw)]);
    setTemplates(tmpl);
    setPlayed(play);
  };

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await load(password);
      setAuthed(true);
      setAuthError('');
    } catch {
      setAuthError('Wrong password.');
    }
  };

  const onSaveEdit = async (id: number) => {
    try {
      const updated = await adminUpdateTemplate(password, id, editState);
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setEditingId(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await adminDeleteTemplate(password, id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  const onCreateNew = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await adminCreateTemplate(password, newTemplate);
      setTemplates((prev) => [...prev, created]);
      setNewTemplate(blankEdit());
      setShowNew(false);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to create');
    }
  };

  const onPromote = async (weapon: PlayedWeapon) => {
    const meta = promoteMeta[weapon.id] ?? {
      difficulty: 'easy',
      location: 'anywhere',
      language: 'en',
    };
    try {
      const created = await adminPromoteWeapon(password, weapon.id, meta);
      setPlayed((prev) =>
        prev.map((w) => (w.id === weapon.id ? { ...w, already_templated: true } : w))
      );
      setTemplates((prev) => {
        const exists = prev.some((t) => t.id === created.id);
        return exists ? prev.map((t) => (t.id === created.id ? created : t)) : [...prev, created];
      });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to promote');
    }
  };

  if (!authed) {
    return (
      <PageShell>
        <Card title="Admin">
          <form onSubmit={onLogin} className={styles.loginForm}>
            <input
              className={styles.input}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            {authError && <p className={styles.error}>{authError}</p>}
            <Button type="submit">Login</Button>
          </form>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${tab === 'templates' ? styles.activeTab : ''}`}
          onClick={() => setTab('templates')}
        >
          Templates ({templates.length})
        </button>
        <button
          className={`${styles.tab} ${tab === 'played' ? styles.activeTab : ''}`}
          onClick={() => setTab('played')}
        >
          Played weapons ({played.filter((w) => !w.already_templated).length} new)
        </button>
      </div>

      {tab === 'templates' && (
        <Card title="Weapon Templates">
          <div className={styles.templateList}>
            {templates.map((t) =>
              editingId === t.id ? (
                <div key={t.id} className={styles.editRow}>
                  <EditForm state={editState} onChange={setEditState} />
                  <div className={styles.rowActions}>
                    <Button onClick={() => onSaveEdit(t.id)}>Save</Button>
                    <Button variant="secondary" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={t.id} className={styles.templateRow}>
                  <div className={styles.templateInfo}>
                    <strong>{t.name}</strong>
                    <span className={styles.badges}>
                      <span className={styles.badge}>{t.difficulty}</span>
                      <span className={styles.badge}>{t.location}</span>
                      <span className={styles.badge}>{t.language}</span>
                    </span>
                    <p className={styles.desc}>{t.description}</p>
                  </div>
                  <div className={styles.rowActions}>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEditingId(t.id);
                        setEditState({
                          name: t.name,
                          description: t.description,
                          difficulty: t.difficulty,
                          location: t.location,
                          language: t.language,
                        });
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="secondary" onClick={() => onDelete(t.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              )
            )}

            {showNew ? (
              <form onSubmit={onCreateNew} className={styles.editRow}>
                <EditForm state={newTemplate} onChange={setNewTemplate} />
                <div className={styles.rowActions}>
                  <Button type="submit">Add</Button>
                  <Button variant="secondary" onClick={() => setShowNew(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <Button variant="secondary" onClick={() => setShowNew(true)}>
                + New template
              </Button>
            )}
          </div>
        </Card>
      )}

      {tab === 'played' && (
        <Card
          title="Played weapons"
          subtitle="Weapons used in games — promote to add to the template library"
        >
          <div className={styles.templateList}>
            {played.length === 0 && <p className={styles.empty}>No played weapons yet.</p>}
            {played.map((w) => (
              <div
                key={w.id}
                className={`${styles.templateRow} ${w.already_templated ? styles.dimmed : ''}`}
              >
                <div className={styles.templateInfo}>
                  <strong>{w.name}</strong>
                  <span className={styles.gameCode}>from game {w.game_code}</span>
                  {w.already_templated && <span className={styles.badge}>already in library</span>}
                  <p className={styles.desc}>{w.description}</p>
                </div>
                {!w.already_templated && (
                  <div className={styles.promoteCol}>
                    <MetaSelect
                      value={
                        promoteMeta[w.id] ?? {
                          difficulty: 'easy',
                          location: 'anywhere',
                          language: 'en',
                        }
                      }
                      onChange={(meta) =>
                        setPromoteMeta((prev) => ({ ...prev, [w.id]: meta as EditState }))
                      }
                    />
                    <Button onClick={() => onPromote(w)}>Add to library</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </PageShell>
  );
}

function EditForm({ state, onChange }: { state: EditState; onChange: (s: EditState) => void }) {
  const set =
    (key: keyof EditState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      onChange({ ...state, [key]: e.target.value });
  return (
    <div className={styles.editForm}>
      <input
        className={styles.input}
        placeholder="Name"
        value={state.name}
        onChange={set('name')}
        required
      />
      <textarea
        className={styles.textarea}
        placeholder="Description"
        value={state.description}
        onChange={set('description')}
        rows={2}
      />
      <div className={styles.selectRow}>
        <select className={styles.select} value={state.difficulty} onChange={set('difficulty')}>
          {DIFFICULTIES.map((d) => (
            <option key={d}>{d}</option>
          ))}
        </select>
        <select className={styles.select} value={state.location} onChange={set('location')}>
          {LOCATIONS.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <select className={styles.select} value={state.language} onChange={set('language')}>
          {LANGUAGES.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function MetaSelect({
  value,
  onChange,
}: {
  value: { difficulty: string; location: string; language: string };
  onChange: (v: { difficulty: string; location: string; language: string }) => void;
}) {
  const set = (key: string) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    onChange({ ...value, [key]: e.target.value });
  return (
    <div className={styles.selectRow}>
      <select className={styles.select} value={value.difficulty} onChange={set('difficulty')}>
        {DIFFICULTIES.map((d) => (
          <option key={d}>{d}</option>
        ))}
      </select>
      <select className={styles.select} value={value.location} onChange={set('location')}>
        {LOCATIONS.map((l) => (
          <option key={l}>{l}</option>
        ))}
      </select>
      <select className={styles.select} value={value.language} onChange={set('language')}>
        {LANGUAGES.map((l) => (
          <option key={l}>{l}</option>
        ))}
      </select>
    </div>
  );
}
